import React, { ChangeEvent, Fragment, useEffect, useState } from 'react';
import dayjs from 'dayjs';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch } from 'hooks/useStore';
import AccountsAsync from 'store/accounts/accountsAsync';
import { accountsActions } from 'store/accounts/accountsSlice';
import AccountFeatures from 'types/AccountFeatures';
import QuizTypes from 'types/QuizTypes';
import AiProviderTypes from 'types/AiProviderTypes';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Checkbox, Select, Toggle } from 'components/Controls';
import { Button } from 'components/Buttons';
import { Loader } from 'components/Utilities';
import DatePicker from 'components/DatePicker';
// Utilities
import { getOptionsFromEnum, quizTypesLabels } from 'utilities/utilities';

type Props = {
  accountId: string;
  open: boolean;
  onClose: () => void;
  isOwner?: boolean; // Optional prop to indicate if the account is owned by the user
}

const AccountsFormDialog:React.FC<Props> = ({ accountId, open, onClose, isOwner = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [features, setFeatures] = useState<AccountFeatures[]>([]);
  const [quizTypes, setQuizTypes] = useState<QuizTypes[]>([]);
  const [licenseExpirationDate, setLicenseExpirationDate] = useState<any>('');
  const [aiProvider, setAiProvider] = useState<string>('');

  const onChangeFeature = (feature: AccountFeatures) => {
    if (features.includes(feature)) {
      setFeatures(prev => prev.filter(f => f !== feature));
    } else {
      setFeatures(prev => [...prev, feature]);
    }
  }

  const onChangeQuizTypes = (quizType: QuizTypes) => {
    if (quizTypes.includes(quizType)) {
      setQuizTypes(prev => prev.filter(type => type !== quizType));
    } else {
      setQuizTypes(prev => [...prev, quizType]);
    }
  }

  const onSubmit = () => {
    setIsUpdating(true);

    const data:any = {
      accountId,
      accountData: {
        features, quizTypes, aiProvider
      }
    }

    data.accountData['licenseExpirationDate'] = licenseExpirationDate
      ? `${dayjs(licenseExpirationDate).format('YYYY-MM-DD')}T00:00:00.000Z` // Format date to ISO string
      : '';

    dispatch(AccountsAsync.updateAccount(data)).unwrap()
      .then(() => onClose())
      .finally(() => setIsUpdating(false));
  };

  useEffect(() => {
    setIsLoading(true);

    dispatch(AccountsAsync.getAccount(accountId)).unwrap()
      .then((data) => {
        setFeatures(data.features || []);
        setQuizTypes(data.quizTypes || []);
        setLicenseExpirationDate(data.licenseExpirationDate ? dayjs(data.licenseExpirationDate).toDate() : '');
        setAiProvider(data.aiProvider || AiProviderTypes.Perplexity);
      })
      .then(() => setIsLoading(false));

    return () => {
      dispatch(accountsActions.setInitialField('account'));
    }
    // eslint-disable-next-line
  }, []);

  const featuresLabels:Record<AccountFeatures, string> = {
    [AccountFeatures.WhiteLabeling]: t('dialogs.accountsForm.whiteLabelingLabel'),
    [AccountFeatures.AIModule]: t('dialogs.accountsForm.aiModuleLabel'),
    [AccountFeatures.QuizAgent]: t('dialogs.accountsForm.quizAgentLabel'),
    [AccountFeatures.BetaQuestions]: t('dialogs.accountsForm.betaQuestionsLabel'),
    [AccountFeatures.Events]: t('dialogs.accountsForm.eventsLabel'),
  }

  const featuresHints:Record<AccountFeatures, string> = {
    [AccountFeatures.WhiteLabeling]: t('dialogs.accountsForm.whiteLabelingHint'),
    [AccountFeatures.AIModule]: '',
    [AccountFeatures.QuizAgent]: '',
    [AccountFeatures.BetaQuestions]: '',
    [AccountFeatures.Events]: '',
  }

  return (
    <Dialog size="lg" open={open} onClose={onClose}>
      {isLoading ? <Loader /> : (
        <form className="flex flex-grow flex-col overflow-y-auto" noValidate>
          <DialogTitle title={t('dialogs.accountsForm.title')} onClose={onClose} />
          <DialogContent>
            {isOwner ? (
              <div className="mb-4" style={{ maxWidth: '347px' }}>
                <DatePicker
                  id="license-expiration-date"
                  name="license-expiration-date"
                  label={t('labels.licenseExpirationDate')}
                  value={licenseExpirationDate}
                  onChange={(date:any) => {
                    console.log(date);
                    setLicenseExpirationDate(date);
                  }}
                  minDate={new Date()}
                  showTimeInput={false}
                />
              </div>
            ) : null}
            <div className="mb-4 w-max">
              {Object.values(AccountFeatures).map((feature) => (
                <Fragment key={`account-feature-${feature}`}>
                  <div className="flex items-center justify-between mb-2" key={feature}>
                    <span className="block text-white text-base font-semibold mr-3">{featuresLabels[feature]}</span>
                    <Toggle
                      id={`account-feature-${feature}`}
                      checked={features.includes(feature)}
                      onChange={() => onChangeFeature(feature)}
                    />
                  </div>
                  {featuresHints[feature] ? (
                    <span className="block text-gray-400 text-sm font-light leading-5 mb-2">{featuresHints[feature]}</span>
                  ) : null}
                </Fragment>
              ))}
            </div>
            <div className="mb-4" style={{ maxWidth: '347px' }}>
              <Select
                value={aiProvider}
                label={t('labels.aiProvider')} id="aiProvider" name="aiProvider"
                required={true}
                options={getOptionsFromEnum(AiProviderTypes)}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setAiProvider(event.target.value)}
              />
            </div>
            <div className="mb-4">
              <p className="text-white text-base font-semibold mb-2">{t('dialogs.accountsForm.quizTypesLabel')}</p>
              {Object.values(QuizTypes).map((quizType) => (
                <Checkbox
                  key={`quiz-type-${quizType}`} id={`quiz-type-${quizType}`} name={`quiz-type-${quizType}`}
                  className="ml-2 mb-2"
                  checked={quizTypes.includes(quizType)}
                  label={quizTypesLabels[quizType]}
                  onChange={() => onChangeQuizTypes(quizType)}
                />
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={onClose}
            >{t('buttons.cancel')}</Button>
            <Button
              className="ml-2"
              variant="contained"
              loading={isUpdating}
              onClick={onSubmit}
            >{t('buttons.save')}</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  )
}

export default AccountsFormDialog;
